import React, { useEffect, useState } from 'react';
import SignInScreen from 'shared/signin/SignInScreen';
import { shouldAutoRedirect, noteRedirect, resetRedirects } from 'shared/signin/signInRedirect';
import { isSigningOut } from 'shared/signin/signOut';
import { useSignInIntro } from 'shared/signin/signInIntro';
import { useAuth } from '../contexts/KeycloakAuthContext';

/**
 * 24K Infinity sign-in (Jean, 30 Oct 2026): no intermediate page. The
 * identity server's themed page owns the fields, password policy, MFA and
 * identity-provider buttons; this shell only shows the branded redirect state
 * while the browser goes there, or the error state when it cannot be reached.
 * It never handles a password.
 */
function Login() {
  const { login, identityUnavailable } = useAuth();
  const [error, setError] = useState(identityUnavailable ? 'unavailable' : '');
  // The start-up intro, when there is one: it fades out before the redirect.
  const intro = useSignInIntro();
  const requestSignIn = intro && intro.requestSignIn;
  const waitForIntro = Boolean(intro && intro.active && !intro.signInReady);

  useEffect(() => {
    if (identityUnavailable) { setError('unavailable'); return undefined; }
    // A sign-in redirect started now would replace the logout one and keep the session.
    if (error || isSigningOut()) return undefined;
    if (!shouldAutoRedirect()) { setError('loop'); return undefined; }
    if (waitForIntro) { requestSignIn(true); return undefined; }
    let alive = true;
    noteRedirect();
    Promise.resolve()
      .then(() => login())
      .catch((err) => {
        console.error('[Keycloak] Sign-in redirect failed:', err);
        if (alive) setError('unavailable');
      });
    return () => { alive = false; };
  }, [error, identityUnavailable, login, waitForIntro, requestSignIn]);

  // An error shows its own screen: the intro must not keep covering it.
  useEffect(() => { if (error && requestSignIn) requestSignIn(false); }, [error, requestSignIn]);

  if (!error) return <SignInScreen product="infinity" context="Provider control plane" />;
  const retry = () => {
    resetRedirects();
    if (error === 'loop') setError('');
    else window.location.reload();
  };
  return <SignInScreen product="infinity" status="error" onRetry={retry} />;
}

export default Login;
